import { getData, getFirst, isValid } from 'clients/Clients';
import { getBannerNano, getCustomerMiniBannerList } from 'clients/MarketingClient';
import { getDataProductList } from 'clients/ProductClientV2';
import {
  getProductRecommendation,
  getWebOrderDetail,
  loadCart,
  loadProductDetail,
  loadProductsFuzzySearch,
  searchFuzzyLite,
} from 'clients/WebServiceClient';
import { ENUM_ORDER_STATUS, ENUM_ORDER_TYPE } from 'constants/Enums';
import { HTTP_STATUS } from 'constants/Enums/http';
import { ENUMS_MINI_BANNER_CAMPAIGN_PLATFORM } from 'constants/Enums/mini-banner';
import { HIGH_PROFIT_TAG, MARGIN_RANGE_PRODUCT, PAGE_SIZE_12, PAGE_SIZE_30, THUOC_VA_HOAT_CHAT } from 'constants/data';
import { PreventSearchKeywords, PreventSearchKeywordsAlias } from 'constants/data/search';
import { convertSlug, mapDataProduct } from 'services/ProductServiceV2';
import { TTL_CACHE_CUSTOMER_TARGET, TTL_CACHE_MINI_BANNER } from 'sysconfig';
import { changeAlias } from 'utils/StringUtils';
import WebServiceUtils from 'utils/WebServiceUtils';
import { encryptedData } from 'utils/helper';
import { CACHE_KEY, getWithExpiry, setWithExpiry } from 'utils/localStorageUtils';

export async function getMiniBanners({ user }) {
  // Kèm provinceCode và customerID vì danh sách phụ thuộc cả hai; hai tài khoản
  // dùng chung trình duyệt không được thấy dữ liệu của nhau.
  const cacheKey = `${CACHE_KEY.MINI_BANNERS}${user?.provinceCode || '00'}_${user?.customerID || 'guest'}`;

  // Case 1: cache hit
  const cachedData = getWithExpiry(cacheKey);

  if (cachedData) {
    return cachedData;
  }

  // Case 2: cache miss -> Fetch missing data, then cache
  const campaignListImageRes = await getCustomerMiniBannerList({
    q: {
      provinceCode: user?.provinceCode || '00',
      platform: ENUMS_MINI_BANNER_CAMPAIGN_PLATFORM.WEB,
    },
    isBasic: !user,
  });

  if (!isValid(campaignListImageRes)) {
    return [];
  }

  const data =
    campaignListImageRes?.data
      ?.sort((b1, b2) => b1.imageIndex - b2.imageIndex)
      ?.map(({ url = '', imageURL = '', code = '', imageIndex = 0 }) => ({ url, imageURL, code, imageIndex })) || [];

  // Update cache
  setWithExpiry(cacheKey, data, { ttl: TTL_CACHE_MINI_BANNER * 1000 });

  return data;
}

export async function getNanoBanners({ provinceCode, customerID }) {
  // Kèm customerID vì danh sách đã phụ thuộc khách; hai tài khoản dùng chung
  // trình duyệt không được thấy dữ liệu của nhau.
  const cacheKey = `${CACHE_KEY.NANO_BANNERS}${provinceCode}_${customerID || 'guest'}`;

  // Case 1: cache hit
  const cachedData = getWithExpiry(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  // Case 2: cache miss -> Fetch missing data, then cache
  const bannerRes = await getBannerNano({
    params: {
      type: 'NANO_BANNER',
      provinceCode,
    },
  });
  const data = getData(bannerRes);

  if (isValid(bannerRes)) {
    const currentTime = new Date();

    data.forEach((item) => {
      if (!item) return;
      if (!item.isVisible) return;

      if (item.startDisplayTime && item.endDisplayTime) {
        const startDisplayTime = new Date(item.startDisplayTime);
        if (currentTime < startDisplayTime) return;

        const endDisplayTime = new Date(item.endDisplayTime);
        if (currentTime > endDisplayTime) return;
      }

      delete item.createdTime;
      delete item.lastUpdatedTime;
      delete item._id;
    });

    // Update cache
    setWithExpiry(cacheKey, data, { ttl: TTL_CACHE_CUSTOMER_TARGET * 1000 });
  }

  return data;
}

// product
async function getDataProductDetail({ ctx, slug = '', isConvertProduct, productDetailPage = false, isGetCrossSell = false }) {
  const params = {};

  if (slug) {
    params.q = convertSlug(slug);
  } else {
    const { query } = ctx;
    params.q = convertSlug(query?.slug);
  }
  const { params: newParams } = WebServiceUtils.addQueryOption({ params, documentFile: true, productDetailPage });
  const result = await loadProductDetail({
    ctx,
    // BV-2271: isGetCrossSell chỉ bật từ lần gọi phía client của trang PDP, không bật ở SSR
    params: { ...newParams, queryOption: newParams?.queryOption + 'getWishList,isGetSKUReplace' + (isGetCrossSell ? ',isGetCrossSell' : '') },
  });
  if (!isValid(result)) return result;
  return mapDataProduct({ ctx, result, isConvertProduct });
}

async function getDataProductBySlugs({ ctx, slugs = [] }) {
  const results = await Promise.all(
    slugs.map((slug) => {
      const params = {
        q: convertSlug(slug),
      };
      const { params: newParams } = WebServiceUtils.addQueryOption({ params });
      return loadProductDetail({ ctx, params: newParams });
    }),
  );
  const result = {
    status: HTTP_STATUS.Ok,
    data: results?.filter((res) => isValid(res))?.map((productRes) => getFirst(productRes)) || [],
  };

  return mapDataProduct({ ctx, result, isGetQuantity: false, isAvailable: true });
}

// search
async function getProductsFuzzySearch({ body, useMapProduct = false, isSkuReplaceProductList = false }) {
  const { body: bodyReq } = WebServiceUtils.addQueryOption({ body });
  const { text } = bodyReq || {};
  // chặn search những keyword cấm
  if (text) {
    const textSearch = text.toLocaleLowerCase().trim();
    const lengthTextSearch = textSearch?.split(' ').length;

    if (
      (lengthTextSearch === 1 && PreventSearchKeywords.indexOf(textSearch) >= 0) ||
      textSearch.toLocaleLowerCase().startsWith('http') ||
      PreventSearchKeywordsAlias.indexOf(changeAlias(textSearch)) >= 0
    ) {
      return { status: HTTP_STATUS.NotFound, msg: 'Keyword is not valid' };
    }
  }
  const result = await loadProductsFuzzySearch({
    body: {
      ...bodyReq,
      queryOption: {
        ...bodyReq?.queryOption,
        getShadowSKU: true,
        getWishList: true,
        isReplacePriceAfterVoucher: bodyReq.isReplacePriceAfterVoucher || false,
      },
    },
  });
  if (!isValid(result)) {
    return result;
  }
  if (useMapProduct) return mapDataProduct({ result, isProductCard: true, isSkuReplaceProductList });

  return getData(result);
}

async function searchByKeywords(keyword = '', hasIngredients = true) {
  const searchStrategy = {
    text: true,
    keyword: true,
    ingredient: hasIngredients,
  };
  const body = {
    text: keyword,
    offset: 0,
    limit: PAGE_SIZE_30,
    searchStrategy,
  };

  return getProductsFuzzySearch({ body });
}

// cart
async function getCart({ ctx } = {}) {
  const cartRes = await loadCart({
    ctx,
    params: {
      queryOption: 'price,getPaymentMethod',
    },
  });

  if (isValid(cartRes)) {
    return getFirst(cartRes);
  }
  return cartRes;
}

// chuyển data mới thành cấu trúc cũ để ít thay đổi nhất
const convertProductLiteToProductNormal = (data) =>
  data?.map(({ name, slug, sellerInfo, productID, displayPriceEncrypt, nameHighlights, matchedIngredients }) => {
    const displayPrice = encryptedData(displayPriceEncrypt);
    return {
      displayPrice,
      name,
      sku: {
        name,
        slug,
        sellerCode: sellerInfo?.sellerCode,
        retailPriceValue: displayPrice,
      },
      product: {
        name,
        productID,
        nameHighlights,
        matchedIngredients,
      },
      slug,
      sellerInfo,
    };
  });

// search lite dành cho mỗi cái search ở NAVBAR
export async function doSearchFuzzyLite(body, getAllDataFromRes = false) {
  const searchRes = await searchFuzzyLite(body);
  const data = convertProductLiteToProductNormal(getData(searchRes));
  if (getAllDataFromRes) {
    return {
      ...searchRes,
      data,
    };
  }
  return data;
}

const MINUTES_30 = 1800000;

export const getOrderDetail = async ({ ctx, orderId, isNewFlow = true }) => {
  const orderRes = await getWebOrderDetail({
    ctx,
    params: { q: JSON.stringify({ orderId, isNewFlow }) },
  });

  if (!isValid(orderRes)) {
    return orderRes;
  }

  const order = getFirst(orderRes, {});

  const { status, createdTime, redeemCode = null, redeemApplyResult = null, totalDiscount = null, orderItems = [], invoiceInfo = {} } = order;

  if (redeemCode?.length > 0 && !redeemApplyResult) {
    const convertRedeem = [];
    if (totalDiscount > 0) {
      convertRedeem.push({
        code: redeemCode[0],
        discountValue: totalDiscount,
        canUse: true,
      });
    } else {
      const skuGift = orderItems?.find((item) => item?.type === 'GIFT')?.sku;
      // Thuy29Dec2022: cần convert vì ko migrate cho đơn hàng cũ
      if (skuGift) {
        convertRedeem.push({
          code: redeemCode[0],
          gifts: [{ sku: skuGift, quantity: 1 }],
          canUse: true,
        });
      }
    }

    order.redeemApplyResult = convertRedeem;
  }

  // logic check update
  // order can edit if status === wait to confirm && 30 minutes
  order.canEdit = status === ENUM_ORDER_STATUS.WAIT_TO_CONFIRM && +new Date() - +new Date(createdTime) <= MINUTES_30;
  // thêm invoices để tránh thay đổi code nhiều
  order.invoices = invoiceInfo?.invoices || [];

  // check can edit :  order have item combo or deal can't edit
  order.canEdit =
    order.canEdit &&
    orderItems.filter(
      (item) => item && (item.type === ENUM_ORDER_TYPE.DEAL || item.type === ENUM_ORDER_TYPE.COMBO || item.type === ENUM_ORDER_TYPE.CAMPAIGN),
    ).length === 0;

  orderRes.data = [order];
  return orderRes;
};

export const buildCategoryFilters = (queryFilter) => {
  if (!queryFilter) return [];

  let categoryFilters = [];
  try {
    categoryFilters = JSON.parse(queryFilter)?.categoryFilters || [];
  } catch (e) {
    categoryFilters = [];
  }

  const combinedCategoryFilters = categoryFilters.filter((item) => item.isCombined) || [];

  return categoryFilters.filter(
    (cat) =>
      cat.code !== cat.categoryCodes?.[0] ||
      cat.efficacyCodes?.length > 0 ||
      combinedCategoryFilters.every((item) => !item.categoryCodes?.includes(cat.code)),
  );
};

export const parseMarginTags = (margin) =>
  String(margin || '')
    .split(',')
    .filter((value) => MARGIN_RANGE_PRODUCT.some((option) => option.value === value));

export const buildHighProfitTagIn = (tag, marginTags) => {
  if (tag !== HIGH_PROFIT_TAG && !marginTags.length) return undefined;
  return marginTags.length && marginTags.length < MARGIN_RANGE_PRODUCT.length ? marginTags : [HIGH_PROFIT_TAG];
};

export const buildPriceRange = (query = {}) => {
  const priceMin = Number(String(query?.priceMin ?? '').replace(/\D/g, '')) || 0;
  const priceMax = Number(String(query?.priceMax ?? '').replace(/\D/g, '')) || 0;
  if (priceMin > 0 && priceMax > 0 && priceMin >= priceMax) return {};
  return {
    ...(priceMin > 0 ? { priceFrom: priceMin } : {}),
    ...(priceMax > 0 ? { priceTo: priceMax } : {}),
  };
};

export const loadDataProductWeb = async ({
  query,
  filter = {},
  isReplacePriceAfterVoucher = false,
  isSkuReplaceProductList = false,
  productListPage = false,
  getTotal = false,
  appSetting,
}) => {
  const highProfitTags = appSetting?.PRICE_RANGE_FILTER?.tags || [];
  const marginTags = parseMarginTags(query.margin);
  const tag = query.tag || (marginTags.length ? HIGH_PROFIT_TAG : '');

  // check currentTab -> filter
  if (tag) {
    if (query.extraTag) {
      filter.tags = [query.extraTag, tag];
      delete filter.tag;
    } else {
      filter.tag = tag;
    }
    // }
  } else if (query.extraTag) {
    filter.tag = query.extraTag;
  }
  const tagIn = buildHighProfitTagIn(tag, marginTags);
  if (tagIn) {
    filter.tagIn = tagIn;
  }

  if (query.manufacturer) {
    filter.manufacturer = query.manufacturer;
  }
  if (query.category) {
    filter.category = query.category;
  }

  if (query.brand) {
    filter.brand = query.brand;
  }

  // tab lợi nhuận cao — nhận diện bằng danh sách tag trong config
  const isHighProfitTab = Boolean(tag) && highProfitTags.includes(tag);

  const { priceFrom, priceTo } = buildPriceRange(query);
  if (priceFrom) {
    filter.priceFrom = priceFrom;
  }
  if (priceTo) {
    filter.priceTo = priceTo;
  }

  if (query.filter) {
    // eslint-disable-next-line no-param-reassign
    filter = {
      ...filter,
      categoryFilters: buildCategoryFilters(query.filter),
    };
  }

  // end
  const page = Number(query?.page - 1 || 0);

  const searchType = Number(query?.searchType || THUOC_VA_HOAT_CHAT);
  const limit = appSetting?.LIMIT_PRODUCT_LIST?.limit || PAGE_SIZE_12;
  const offset = page * limit;
  const body = {
    ...query,
    text: query?.q || query?.text || null,
    offset: offset,
    limit: limit,
    getTotal,
    filter,
    sort: query?.sort || '',
    isAvailable: query?.isAvailable === 'true',
    searchStrategy: {
      text: true,
      keyword: true,
      ingredient: searchType === THUOC_VA_HOAT_CHAT,
    },
    isReplacePriceAfterVoucher,
  };

  return getDataProductList({
    body: {
      ...body,
      queryOption: {
        ...body?.queryOption,
        getShadowSKU: true,
        getWishList: true,
        isReplacePriceAfterVoucher: body.isReplacePriceAfterVoucher || false,
        isSkuReplaceProductList,
        // Tab lợi nhuận cao: tắt SP thay thế. Chúng được gắn lồng vào từng SP nên không đi qua
        // filter khoảng giá, và bản thân SP thay thế có thể không thuộc tag của tab.
        isGetSKUReplace: !(isHighProfitTab || marginTags.length > 0),
        productListPage,
      },
    },
  });
};

export async function getProductRecommendationData({ ctx, q = {}, offset = 0, limit = 20, ...options } = {}) {
  const result = await getProductRecommendation({
    ctx,
    body: {
      q,
      offset,
      limit,
      queryOption: {
        isReplacePriceAfterVoucher: true,
      },
      ...options,
    },
  });
  if (!isValid(result)) return result;

  return mapDataProduct({ result });
}

export default {
  getDataProductDetail,
  getDataProductBySlugs,
  getProductsFuzzySearch,
  searchByKeywords,
  getCart,
  getOrderDetail,
  getDataProductList,
  loadDataProductWeb,
};
